'use client'

import * as React from 'react'
import { type JSONContent } from '@tiptap/core'
import { createTiptapDiff, getDiffSummary, type TiptapDiffResult } from '@/utils/tiptap-diff'

// Normalization utility to handle both TipTap and legacy Plate.js formats
function normalizeContentForDiff(content: unknown): JSONContent {
  if (!content) {
    return { type: 'doc', content: [] }
  }
  
  // If it's already a JSONContent with type field, return as is
  if (typeof content === 'object' && content !== null && 'type' in content) {
    return content as JSONContent
  }
  
  // If it's an array (legacy Plate.js format), wrap it in a doc
  if (Array.isArray(content)) {
    return {
      type: 'doc',
      content: content as JSONContent[]
    }
  }
  
  // Fallback: treat as unknown content
  return { type: 'doc', content: [] }
}
import { EnhancedDiffRenderer } from './diff-renderer-enhanced'
import { cn } from '@/lib/utils'

// Main component props
export interface StableDiffEditorProps {
  /** Current version of the content */
  current: unknown // Support both JSONContent and legacy Plate.js Value
  /** Previous version of the content to compare against */
  previous: unknown // Support both JSONContent and legacy Plate.js Value
  /** Optional className for styling */
  className?: string
  /** Optional placeholder text when content is empty */
  placeholder?: string
  /** Optional callback when diff is calculated */
  onDiffCalculated?: (diff: TiptapDiffResult) => void
  /** Whether to use enhanced rendering similar to TipTap's snapshot comparison */
  useEnhancedRenderer?: boolean
}

/**
 * StableDiffEditor - A stable wrapper around DiffEditor that prevents infinite loops
 * Uses deep equality checks to ensure stable memoization and prevent unnecessary re-renders
 * 
 * @example
 * ```tsx
 * <StableDiffEditor 
 *   current={newContent} 
 *   previous={oldContent}
 *   className="my-4"
 * />
 * ```
 */
export function StableDiffEditor({ 
  current, 
  previous, 
  className,
  placeholder = "No content to compare",
  onDiffCalculated,
  useEnhancedRenderer = true
}: StableDiffEditorProps) {
  
  // Normalize and memoize current content with deep equality check
  const stableCurrent = React.useMemo(() => normalizeContentForDiff(current), [current])
  
  // Normalize and memoize previous content with deep equality check  
  const stablePrevious = React.useMemo(() => normalizeContentForDiff(previous), [previous])

  // Calculate diff and memoize result
  const diffResult = React.useMemo(() => {
    if (!stableCurrent || !stablePrevious) {
      return null
    }
    
    return createTiptapDiff(stablePrevious, stableCurrent)
  }, [stableCurrent, stablePrevious])

  // Call diff calculated callback
  React.useEffect(() => {
    if (diffResult && onDiffCalculated) {
      onDiffCalculated(diffResult)
    }
  }, [diffResult, onDiffCalculated])

  // Additional stability check - only re-render if content actually changed
  const shouldRender = React.useMemo(() => {
    // Check if we have valid content and changes
    return diffResult?.hasChanges ?? false
  }, [diffResult])

  // Handle empty or identical content
  if (!shouldRender) {
    return (
      <div className={cn(
        "flex items-center justify-center min-h-[100px] rounded-md border border-dashed bg-muted/50 text-muted-foreground text-sm",
        className
      )}>
        {placeholder}
      </div>
    )
  }

  // Render the actual diff visualization
  if (!diffResult) {
    return (
      <div className={cn(
        "flex items-center justify-center min-h-[100px] rounded-md border border-dashed bg-muted/50 text-muted-foreground text-sm",
        className
      )}>
        Unable to calculate diff
      </div>
    )
  }

  if (useEnhancedRenderer) {
    return (
      <div className={cn("border rounded-md", className)}>
        {/* Header with summary */}
        <div className="border-b bg-muted/30 p-4 rounded-t-md">
          <div className="flex items-center justify-between">
            <div className="text-sm font-medium">
              {getDiffSummary(diffResult)}
            </div>
            <div className="text-xs text-muted-foreground">
              {diffResult.stats.added} added, {diffResult.stats.modified} modified, {diffResult.stats.deleted} deleted
            </div>
          </div>
        </div>
        
        {/* Enhanced Diff Renderer */}
        <div className="p-6">
          <EnhancedDiffRenderer
            diffResult={diffResult}
            previous={stablePrevious}
            current={stableCurrent}
          />
        </div>
      </div>
    )
  }

  // Fallback to basic renderer (keeping original implementation)
  return (
    <div className={cn("border rounded-md p-4", className)}>
      <div className="text-sm text-muted-foreground mb-4">
        <strong>Changes:</strong> {getDiffSummary(diffResult)}
      </div>
      <div className="text-xs text-muted-foreground">
        Basic diff view - set useEnhancedRenderer={true} for TipTap-style visualization
      </div>
    </div>
  )
}

// Re-export types
export type { JSONContent, TiptapDiffResult } 